// mandatoryPhraseCodeLabel — translate a `mandatoryPhrases.missingCodes` entry
// (analysis-metric-semantics, Task 3) into a label a person reads.
//
// The model now reports a missing requirement as one of MANDATORY_PHRASE_CODES
// (@aica/core) — a fixed English code, not translated prose — so the same
// requirement groups and can be targeted by a rule regardless of the
// analysis's language. Somewhere has to turn that code back into a sentence;
// this is that place, shared by both analysis-detail renderers (v1
// SpeechQualitySection, v2 ComplianceSection) so a new code needs a label
// added in exactly one spot, not once per consumer.
//
// Labels live in i18n `records.json` → `records.analysisPanel.
// mandatoryPhraseCodes.<CODE>` (all four locales) — the single source for
// these labels. A backend copy (`packages/core/src/i18n/mandatory-phrase-
// codes.ts`) existed briefly for a speculative future server-rendered
// surface and was removed in review: nothing read it, and every surface
// that prints analysis text server-side (CRM timeline, alert email) has its
// own separate i18n mechanism already, none of which is this one — so there
// was no real second reader to keep in sync with. Add a backend copy only
// when a concrete consumer needs it, wired to that consumer's actual i18n
// system, not speculatively ahead of one.
import type { TFunction } from 'i18next';

/**
 * English fallback per code — used by react-i18next when a locale's JSON key
 * is missing, and by every unit test (the shared `react-i18next` test mock
 * returns this fallback verbatim instead of resolving a real locale file).
 * Mirror of `records.json`'s `en` copy.
 */
const FALLBACK_LABELS: Record<string, string> = {
  RECORDING_DISCLAIMER: 'Recording disclosure was not given',
  COMPANY_NAME: 'Company name was not stated',
  CALLBACK_OPTION: 'Callback/follow-up option was not offered',
  SATISFACTION_GUARANTEE: 'Satisfaction guarantee was not mentioned',
};

/**
 * Translate one `missingCodes` entry via the `records` i18n namespace.
 *
 * An unrecognized code (a future code not yet deployed to this frontend
 * build, or corrupted data) falls back to the raw code string rather than
 * throwing or rendering nothing — same "degrade, don't blank" posture as the
 * `missing` free-text path it complements.
 */
export function mandatoryPhraseCodeLabel(code: string, t: TFunction): string {
  // The `records:` prefix names the namespace explicitly, so this works with a
  // `t` bound to ANY namespace — the alert rule builder (`useTranslation
  // ('alerts')`) labels the same codes in its value picker. Without the prefix
  // that call site would silently render the English fallback while the
  // translations sat one namespace away. All namespaces are preloaded at init
  // (i18n/config.ts `ns`), so naming one costs no extra fetch.
  return t(
    `records:records.analysisPanel.mandatoryPhraseCodes.${code}`,
    FALLBACK_LABELS[code] ?? code
  );
}
